import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { SxProps, DialogActions, Stack } from '@mui/material';
import { vars } from '../../../../../Theme';
import { mdiSitemapOutline } from '@mdi/js';
import CtaButton from '../../_parts/CtaButton';
import { useDispatch, useSelector } from 'react-redux';
import {
    getCurrentTabsForwardingDataValidOptions,
    getDocuments,
    getForwardTabs,
    getTotalForwardedDocuments,
    onConfirm,
} from '../../../../../redux/project/sliceProjectWorkflow';
import { IAppReduxStore } from '../../../../../redux/store';
import ActionButton from '../../../ActionButton/ActionButton';
import { useParams } from 'react-router-dom';
import SelectedPaths from './SelectedPaths';

const dialogActionsSx: SxProps = {
    px: 3,
    py: 1,
    borderTop: `1px solid ${vars.lightGray}`,
    justifyContent: 'space-between',
};

const actionButtonProps: SxProps = {
    flexDirection: 'row',
    fontSize: 14,
    gap: 1,
    '&:hover': {
        backgroundColor: 'transparent',
        color: vars.black,
    },
};

export default ({ setHasError, getStateChanged, mainState }) => {
    const { t } = useTranslation('translations', { keyPrefix: 'mainDialog' });
    const dispatch: Dispatch<unknown> = useDispatch();
    const { replicaId } = useParams();

    const forwardData = useSelector(getCurrentTabsForwardingDataValidOptions);
    const selectedTab = useSelector((_store: IAppReduxStore) => _store.projectWorkflow.selectedTab);
    const isLoading = useSelector((_store: IAppReduxStore) => _store.projectWorkflow.loading);

    const tabs = useSelector(getForwardTabs);
    const documents = useSelector(getDocuments);
    const totalDocuments = useSelector(getTotalForwardedDocuments);

    const selectedPathes = SelectedPaths({ mainState, tabs });

    const handleConfirm = () => {
        dispatch(onConfirm({ pathes: selectedPathes, replicaId }));
    };

    const isNestedLevelUnselected = () => {
        const selectedOption = forwardData.filter(
            item => item.path === selectedPathes[selectedTab],
        );
        if (selectedOption.length) {
            return selectedOption[0].connectorSubSelection.length > 0;
        }
        return false;
    };

    const handleProceed = (handleAction: () => void) => {
        if (documents.length === 0 || forwardData.length === 0) {
            getStateChanged({
                parentValue: tabs[selectedTab].name,
                state: { exclusive: ['placeholder'], multi: [] },
            });
        }
        if (
            isNestedLevelUnselected() ||
            (selectedPathes[selectedTab] === undefined &&
                forwardData.length !== 0 &&
                documents.length > 0)
        ) {
            setHasError(true);
        } else {
            handleAction();
        }
    };

    const handleOpenDiagram = () => {
        if (!documents.length) return;

        // 8116: encode '/' in DocNumber
        const encDocNumber = `${documents[0].documentNumber}`.replace('/', '%2F');

        window.open(`/workflow-loader/${replicaId}/${documents[0].universalId}/${encDocNumber}`);
    };

    return (
        <DialogActions sx={dialogActionsSx}>
            <ActionButton
                icon={mdiSitemapOutline}
                text={t('openDiagram')}
                onClick={handleOpenDiagram}
                sx={actionButtonProps}
                disabled={isLoading}
            />
            <Stack sx={{ gap: 0.5 }}>
                <CtaButton
                    variant='contained'
                    text={selectedTab === tabs.length - 1 ? t('confirm') : t('next')}
                    onClick={() => handleProceed(handleConfirm)}
                    disabled={isLoading || totalDocuments === 0}
                />
            </Stack>
        </DialogActions>
    );
};

export interface ForwardState {
    multi: string[];
    exclusive: string[];
}
